/**
 * O briefing high-ticket (Diagnóstico Cirúrgico, Método LL movimento I).
 * Fonte: docs/00F. Schema orientado a dados: uma seção vira um bloco na
 * tela, um campo vira uma pergunta. As respostas ficam em `responses[key]`.
 *
 * Vive em @unik/shared (não só no front) porque o Worker também precisa dele
 * para semear o cliente de demonstração com respostas nas mesmas chaves —
 * uma cópia duplicada em dois lugares seria terreno fértil para drift.
 */

export interface BriefingField {
  key: string;
  label: string;
  type: "text" | "textarea" | "select";
  options?: readonly string[];
  placeholder?: string;
  hint?: string;
}

export interface BriefingSection {
  key: string;
  title: string;
  description: string;
  fields: BriefingField[];
}

/**
 * Agrupamento das seções em etapas do assistente de auto-cadastro (docs/00G §1).
 * A Etapa 1 (nome/e-mail/senha) não está aqui — ela cria a conta, não respostas
 * de briefing; por isso "contato" abaixo não repete nome/e-mail (já capturados lá).
 */
export interface BriefingStep {
  key: string;
  title: string;
  sectionKeys: string[];
}

export const BRIEFING_STEPS: BriefingStep[] = [
  { key: "empresa", title: "Sua empresa e redes", sectionKeys: ["contato", "redesAtuais"] },
  { key: "negocio", title: "Negócio", sectionKeys: ["negocio"] },
  { key: "marca", title: "Marca e posicionamento", sectionKeys: ["posicionamento"] },
  { key: "publico", title: "Seu público", sectionKeys: ["publico"] },
  { key: "conteudo", title: "Conteúdo e metas", sectionKeys: ["conteudo", "metas"] },
  { key: "operacao", title: "Operação e mais", sectionKeys: ["operacao", "provas"] },
];

export const BRIEFING_SCHEMA: BriefingSection[] = [
  {
    key: "contato",
    title: "Sua empresa",
    description: "Os detalhes de contato e da empresa.",
    fields: [
      { key: "cargo", label: "Seu cargo ou função", type: "text", placeholder: "Ex.: sócia, gerente de marketing" },
      { key: "whatsappContato", label: "WhatsApp para contato", type: "text", placeholder: "+55 31 9..." },
      { key: "nomeEmpresa", label: "Nome da empresa / marca", type: "text" },
      { key: "site", label: "Site (se tiver)", type: "text" },
    ],
  },
  {
    key: "redesAtuais",
    title: "Redes sociais atuais",
    description: "De onde estamos partindo.",
    fields: [
      { key: "instagramAtual", label: "@ do Instagram", type: "text", placeholder: "@suaempresa" },
      { key: "seguidoresAtuais", label: "Quantos seguidores tem hoje?", type: "text" },
      { key: "outrasRedes", label: "Outras redes ativas (Facebook, TikTok, YouTube, LinkedIn...)", type: "textarea" },
      { key: "quemTemAcesso", label: "Quem tem acesso às redes hoje?", type: "textarea" },
      {
        key: "avaliacaoAtual", label: "Como você avalia o desempenho atual do seu Instagram?", type: "select",
        options: ["Ótimo", "Bom", "Regular", "Ruim", "Não sei avaliar"],
      },
      { key: "oQueIncomoda", label: "O que mais te incomoda no que existe hoje?", type: "textarea" },
    ],
  },
  {
    key: "negocio",
    title: "Negócio & oferta",
    description: "O que você vende e para onde quer chegar.",
    fields: [
      { key: "oferta", label: "O que você vende hoje?", type: "textarea" },
      { key: "ticket", label: "Ticket médio atual", type: "text", placeholder: "Ex.: R$ 800" },
      { key: "faturamento", label: "Faturamento médio mensal", type: "text", placeholder: "Ex.: R$ 40.000" },
      { key: "sazonalidade", label: "Seu negócio tem sazonalidade? Quando é mais forte/fraco?", type: "textarea" },
      { key: "concorrentes", label: "Principais concorrentes (nomes ou @)", type: "textarea" },
      { key: "meta", label: "Meta para os próximos 6 meses", type: "textarea" },
    ],
  },
  {
    key: "posicionamento",
    title: "Posicionamento & marca",
    description: "Como você quer ser vista(o) no mercado.",
    fields: [
      { key: "comoQuerSerVista", label: "Como você quer ser vista(o) pelo seu público?", type: "textarea" },
      { key: "diferencial", label: "O que te diferencia da concorrência?", type: "textarea" },
      { key: "autoridade", label: "Do que você é autoridade? Por quê?", type: "textarea" },
      { key: "origemMarca", label: "Qual a história ou origem da marca?", type: "textarea" },
      { key: "premios", label: "Prêmios, certificações ou reconhecimentos?", type: "textarea" },
    ],
  },
  {
    key: "publico",
    title: "Público-alvo",
    description: "Quem é o cliente ideal.",
    fields: [
      { key: "clienteIdeal", label: "Descreva o seu cliente ideal", type: "textarea" },
      { key: "faixaEtaria", label: "Faixa etária predominante", type: "text", placeholder: "Ex.: 30 a 45 anos" },
      { key: "localizacaoPublico", label: "Localização geográfica do público", type: "text" },
      { key: "dores", label: "Quais as principais dores dele?", type: "textarea" },
      { key: "desejos", label: "O que ele mais deseja?", type: "textarea" },
      { key: "objecoes", label: "Quais objeções ele costuma ter antes de comprar?", type: "textarea" },
    ],
  },
  {
    key: "conteudo",
    title: "Conteúdo & identidade visual",
    description: "Como a sua marca deve soar e parecer.",
    fields: [
      { key: "tomDeVoz", label: "Tom de voz (formal, próximo, técnico...)", type: "text" },
      { key: "paleta", label: "Cores e identidade visual (se já existir)", type: "text" },
      { key: "referencias", label: "Perfis/marcas de referência", type: "textarea" },
      { key: "nuncaPostar", label: "O que NUNCA pode aparecer no seu conteúdo?", type: "textarea" },
      {
        key: "qualidadeMaterial", label: "Você tem fotos/vídeos próprios de qualidade?", type: "select",
        options: ["Sim, bastante", "Um pouco", "Não tenho"],
      },
      {
        key: "precisaProducao", label: "Precisa de banco de imagens ou produção de conteúdo?", type: "select",
        options: ["Sim", "Não", "Não sei"],
      },
    ],
  },
  {
    key: "metas",
    title: "Metas & expectativas",
    description: "O que prova que deu certo.",
    fields: [
      { key: "metaLongoPrazo", label: "O que espera alcançar com o Instagram em 6 a 12 meses?", type: "textarea" },
      {
        key: "metricaImportante", label: "Qual métrica importa mais pra você?", type: "select",
        options: ["Seguidores", "Alcance", "Engajamento", "Vendas diretas", "Agendamentos/consultas"],
      },
      {
        key: "tempoTrafegoPago", label: "Tem verba para tráfego pago (impulsionamento/Meta Ads)?", type: "select",
        options: ["Sim", "Não", "Ainda não, mas pretendo"],
      },
      { key: "orcamentoTrafego", label: "Orçamento mensal estimado para tráfego pago (se houver)", type: "text" },
    ],
  },
  {
    key: "operacao",
    title: "Operação e logística",
    description: "Os detalhes práticos para começarmos.",
    fields: [
      { key: "responsavelAprovacao", label: "Quem é o responsável pelas aprovações?", type: "text" },
      { key: "frequencia", label: "Frequência de postagem desejada", type: "text", placeholder: "Ex.: 3x por semana" },
      { key: "datasSazonais", label: "Datas ou eventos importantes/sazonais a considerar", type: "textarea" },
      { key: "restricaoLegal", label: "Alguma restrição legal ou regulatória (ex.: conselho de classe)?", type: "textarea" },
      { key: "restricaoHorario", label: "Alguma restrição de horário ou dia para postar?", type: "textarea" },
    ],
  },
  {
    key: "provas",
    title: "Provas sociais",
    description: "O que ajuda a provar resultado.",
    fields: [
      {
        key: "temDepoimentos", label: "Tem depoimentos de clientes disponíveis?", type: "select",
        options: ["Sim", "Não"],
      },
      { key: "resultadosCompartilhar", label: "Pode compartilhar prints de resultados/depoimentos?", type: "textarea" },
      { key: "recadoLivre", label: "Algo mais que a gente precisa saber?", type: "textarea" },
    ],
  },
];

/**
 * Respostas plausíveis para o cliente de demonstração (docs/00F §2). Fica ao
 * lado do schema, com as MESMAS chaves, para nunca dessincronizar.
 */
export const DEMO_BRIEFING_RESPONSES: Record<string, string> = {
  cargo: "Sócia-proprietária",
  whatsappContato: "+55 31 99887-6655",
  nomeEmpresa: "Clínica Aurora Estética",
  site: "www.clinicaauroraestetica.com.br",

  instagramAtual: "@clinicaauroraestetica",
  seguidoresAtuais: "2.600",
  outrasRedes: "Facebook (pouco ativo) e um canal no WhatsApp para novidades.",
  quemTemAcesso: "Eu e a recepcionista Camila, que responde as mensagens diretas.",
  avaliacaoAtual: "Regular",
  oQueIncomoda: "Posto sem estratégia, sempre em cima da hora, e não sei se está trazendo pacientes de verdade.",

  oferta: "Harmonização facial, preenchimento labial, botox e limpeza de pele.",
  ticket: "R$ 900",
  faturamento: "R$ 42.000",
  sazonalidade: "Mais forte perto do verão e datas como Dia das Mães; mais fraco em janeiro.",
  concorrentes: "@espacobelezamg e @drarenataestetica, ambas na mesma região.",
  meta: "Dobrar o número de agendamentos por indicação do Instagram em 6 meses.",

  comoQuerSerVista: "Como uma clínica de confiança, técnica e humana — não só estética, mas cuidado de verdade.",
  diferencial: "Atendimento individualizado com plano de tratamento personalizado, não é 'linha de produção'.",
  autoridade: "Sou formada em harmonização facial avançada, com mais de 8 anos de experiência.",
  origemMarca: "Comecei atendendo em casa há 10 anos, hoje tenho consultório próprio há 3 anos.",
  premios: "Nenhum prêmio ainda, mas tenho certificação internacional em toxina botulínica.",

  clienteIdeal: "Mulheres de 30 a 50 anos, classe média-alta, que valorizam resultado natural.",
  faixaEtaria: "30 a 50 anos",
  localizacaoPublico: "Belo Horizonte e região metropolitana",
  dores: "Insegurança com a aparência, medo de resultado exagerado ('cara de plástico').",
  desejos: "Parecer descansada e natural, sem que ninguém perceba que 'mexeu em algo'.",
  objecoes: "Medo da dor, medo do preço, medo de não ficar natural.",

  tomDeVoz: "Acolhedor e próximo, mas sempre técnico quando fala de procedimento.",
  paleta: "Rosa nude, dourado e branco — já tem manual de marca simples.",
  referencias: "Gosta do perfil @drapatriciaestetica como referência de conteúdo.",
  nuncaPostar: "Fotos de sangue, before/after muito agressivo, promessas de milagre.",
  qualidadeMaterial: "Um pouco",
  precisaProducao: "Sim",

  metaLongoPrazo: "Ser a clínica de referência em harmonização facial natural na região.",
  metricaImportante: "Agendamentos/consultas",
  tempoTrafegoPago: "Ainda não, mas pretendo",
  orcamentoTrafego: "R$ 1.000 a partir do 3º mês",

  responsavelAprovacao: "A própria Fernanda aprova tudo.",
  frequencia: "3x por semana + Stories diários",
  datasSazonais: "Dia das Mães, Black Friday e proximidade do verão.",
  restricaoLegal: "Segue as normas do Conselho Federal de Odontologia/Medicina para propaganda.",
  restricaoHorario: "Prefere que os posts não saiam durante os atendimentos, entre 9h e 18h.",

  temDepoimentos: "Sim",
  resultadosCompartilhar: "Tenho vários prints de WhatsApp de pacientes satisfeitas, posso mandar.",
  recadoLivre: "Quero que o Instagram pareça uma clínica de verdade, não um perfil genérico de estética.",
};
